/**
 * Cart Service - Backend API integration for shopping cart
 * 
 * All endpoints require authentication.
 * 
 * Endpoints:
 * GET    /api/cart           - Get user's cart with items
 * POST   /api/cart/items     - Add item to cart
 * PATCH  /api/cart/items/:id - Update cart item quantity
 * DELETE /api/cart/items/:id - Remove item from cart
 */

import api from './api';

const cartService = {
  /**
   * Get the current user's cart
   * Returns cart with items and product details
   */
  getCart: async () => {
    const response = await api.get('/cart');
    return response.data;
  },

  /**
   * Add item to cart
   * @param {number} productId - Product ID to add
   * @param {number} quantity - Quantity to add (default: 1)
   */
  addItem: async (productId, quantity = 1, flashSku = null, variantId = null) => {
    const body = { productId: parseInt(productId), quantity: parseInt(quantity) };
    if (flashSku) body.flashSku = flashSku;
    if (variantId) body.variantId = variantId;
    const response = await api.post('/cart/items', body);
    return response.data;
  },

  /**
   * Update cart item quantity
   * @param {number} cartItemId - Cart item ID (not product ID)
   * @param {number} quantity - New quantity
   */
  updateItem: async (cartItemId, quantity) => {
    const response = await api.patch(`/cart/items/${cartItemId}`, {
      quantity: parseInt(quantity)
    });
    return response.data;
  },

  /**
   * Remove item from cart
   * @param {number} cartItemId - Cart item ID to remove
   */
  removeItem: async (cartItemId) => {
    const response = await api.delete(`/cart/items/${cartItemId}`);
    return response.data;
  },

  /**
   * Clear all items from cart
   * Note: Backend may not have this endpoint - implemented by removing each item
   */
  clearCart: async () => {
    const cart = await cartService.getCart();
    if (cart && cart.items) {
      await Promise.all(cart.items.map(item => cartService.removeItem(item.id)));
    }
    return { items: [] };
  },

  /**
   * Sync local cart to server (for when user logs in)
   * Takes local cart items and adds them to server cart
   */
  syncLocalCart: async (localItems) => {
    if (!localItems || localItems.length === 0) return;
    
    // Add each local item to server cart
    for (const item of localItems) {
      try {
        // Flash items have id=flashSku (string) — use productId (numeric) for the backend
        const numericId = item.productId || item.id;
        await cartService.addItem(numericId, item.quantity, item.flashSku ?? null);
      } catch (error) {
        console.warn('Failed to sync cart item:', item, error);
      }
    }
    
    // Return the updated server cart
    return await cartService.getCart();
  },

  // ===== Guest Cart Methods =====

  getGuestCart: async (sessionId) => {
    const response = await api.get(`/cart/guest?sessionId=${sessionId}`);
    return response.data;
  },

  addGuestItem: async (sessionId, productId, quantity = 1, flashSku = null, variantId = null) => {
    const body = { sessionId, productId: parseInt(productId), quantity: parseInt(quantity) };
    if (flashSku) body.flashSku = flashSku;
    if (variantId) body.variantId = variantId;
    const response = await api.post('/cart/guest/items', body);
    return response.data;
  },

  removeGuestItem: async (sessionId, cartItemId) => {
    const response = await api.delete(`/cart/guest/items/${cartItemId}?sessionId=${sessionId}`);
    return response.data;
  },

  syncGuestCart: async (sessionId, localItems) => {
    if (!localItems || localItems.length === 0) return;
    try {
      const existingCart = await cartService.getGuestCart(sessionId);
      if (existingCart?.items?.length) {
        await Promise.all(existingCart.items.map(i => cartService.removeGuestItem(sessionId, i.id)));
      }
    } catch (e) {
      // Cart doesn't exist yet
    }
    for (const item of localItems) {
      try {
        await cartService.addGuestItem(sessionId, item.productId || item.id, item.quantity, item.flashSku ?? null, item.variantId ?? null);
      } catch (error) {
        console.warn('Failed to sync guest cart item:', item, error);
      }
    }
  },
};

export default cartService;